import {Component, NgZone, OnInit} from '@angular/core';
import {FormBuilder} from '@angular/forms';
import {DialogService} from '../../../services/dialog.service';
import {BehaviorSubject} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {MatTableDataSource} from '@angular/material/table';
import {UploadErrorsService} from '../../../services/upload-errors.service';
import {UserService} from '../../../services/user.service';
import {environment} from '../../../../environments/environment';
import {BlobUploadInfo} from '../../../models/gcs-blob.model';
import {CustomSnackBarService} from '../../../services/custom-snackbar.service';
import {TranslateService} from '@ngx-translate/core';
import {DEFAULT_UPLOAD_ERRORS_ITEMS_PAGE_NUMBER} from '../../../constants/upload-errors-constants';
import {AuthService} from '../../../services/auth.service';
import {Scope} from '../../../constants/scopes';
import {ActivatedRoute} from '@angular/router';
import {extractErrorMessage} from '../../../utils/error-utils';
import {PageEvent} from "@angular/material/paginator";
import {ErrorFile} from "../../../models/error-file";

@Component({
  selector: 'app-upload-errors',
  templateUrl: './upload-errors.component.html',
  styleUrls: ['./upload-errors.component.scss']
})
export class UploadErrorsComponent implements OnInit {

  scopes = [Scope.EMAIL, Scope.PROFILE, Scope.OPENID, Scope.STORAGE].join(' ');
  public errorFilesCount: number;
  public loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();
  public dataSource: MatTableDataSource<ErrorFile> = new MatTableDataSource();

  public columns = [
    {name: 'attachment', show: true},
    {name: 'document', show: true},
    {name: 'step1', show: true},
    {name: 'step2', show: true},
    {name: 'status', show: true}
  ];
  private currentPage: number;

  constructor(private formBuilder: FormBuilder,
              private ngZone: NgZone,
              private activatedRoute: ActivatedRoute,
              private authService: AuthService,
              private userService: UserService,
              private uploadErrorsService: UploadErrorsService,
              private customSnackBarService: CustomSnackBarService,
              private translate: TranslateService,
              private dialogService: DialogService) {
  }

  async ngOnInit(): Promise<void> {
    this.currentPage = 0;
    this.listErrors();
  }

  listErrors(): void {
    this.loadingSubject.next(true);
    this.uploadErrorsService.list(this.currentPage).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe({
      next: (resp) => {
        this.dataSource.data = resp.fileManualUploads;
        this.errorFilesCount = resp.totalFiles;
      }, error: (error) => {
        this.dialogService.error(extractErrorMessage(error));
      }
    });
  }

  async upload(event: Event, element: ErrorFile): Promise<void> {
    this.loadingSubject.next(true);
    const getUploadErrorBucketResponse = await this.uploadErrorsService.getUploadErrorBucket().toPromise();
    this.uploadErrorsService.insertObject(getUploadErrorBucketResponse.bucketName, event).subscribe({
      next: (resp) => {
        (event.target as HTMLButtonElement).disabled = true;
        this.loadingSubject.next(false);
        this.customSnackBarService.openSuccessSnackbar(this.translate.instant('holds.upload.uploadStartedMessage'));
        this.triggerCopy(resp, event, element);
        this.listErrors();
      }, error: (error) => {
        this.loadingSubject.next(false);
        this.dialogService.error(extractErrorMessage(error));
      }
    });
  }

  triggerCopy(uploadResponse: BlobUploadInfo, event: Event, attachment: ErrorFile): void {
    this.uploadErrorsService.copy(uploadResponse, attachment).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe({
      next: () => {},
      error: (error) => {
        console.log(error);
        (event.target as HTMLButtonElement).disabled = false;
        this.dialogService.error(extractErrorMessage(error));
      }
    });
  }

  displayedColumns(): string[] {
    return this.columns.filter(column => column.show).map(column => column.name);
  }

  openDocumentPage(document: ErrorFile): void {
    const url = this.documentLink(document);
    window.open(url, '_blank').focus();
  }

  documentLink(document: ErrorFile): string {
    const aodocsUrl = environment.aodocsUrl;
    const domain = this.userService.user.domain;
    return aodocsUrl + '?aodocs-domain=' + domain + '#Menu_viewDoc/LibraryId_' + document.libraryId + '/DocumentId_' + document.documentId;
  }

  public getPageSize(): number {
    return DEFAULT_UPLOAD_ERRORS_ITEMS_PAGE_NUMBER;
  }

  async onUploadLabelClick(event: Event): Promise<void> {
    if (!this.authService.getScopes()?.includes(Scope.STORAGE)) {
      event.preventDefault();
      try {
        await this.authService.requestGoogleStorageScope(this.scopes);
      } catch(e) {
        console.log(e)
        this.dialogService.error('Failed to grant Google Storage access. Please try again.');
      }
    }
  }

  onPageChange($event: PageEvent): void {
    this.currentPage = $event.pageIndex;
    this.listErrors();
  }
}
